import type { PrescriptionDrugInfoResponse } from '@/entities/prescription/api/prescription.api';
import type { EditedOcrDrugRow } from '@/pages/prescriptions/prescriptionOcrUtils';

let tempRowCounter = 0;

export const createTempRowKey = (prefix = 'ocr') =>
  `${prefix}-${Date.now()}-${tempRowCounter++}`;

export type StagingPrescriptionDrug = Omit<
  PrescriptionDrugInfoResponse,
  'prescriptionMrId' | 'isDrugInfoExists'
> & {
  prescriptionMrId?: number | null;
  /** null/undefined = 일괄 추가 전(레거시 3-state) */
  isDrugInfoExists?: boolean | null;
  __tempRowKey?: string;
};

export const getPrescriptionRowKey = (
  record: Pick<StagingPrescriptionDrug, 'prescriptionMrId' | '__tempRowKey'>,
) =>
  record.prescriptionMrId != null
    ? String(record.prescriptionMrId)
    : (record.__tempRowKey ?? '');

export const isStagingDrugRow = (record: StagingPrescriptionDrug) =>
  record.prescriptionMrId == null;

export const withPrescriptionTempRowKey = (
  item: Partial<StagingPrescriptionDrug>,
): StagingPrescriptionDrug => ({
  prescriptionMrId: null,
  drugCode: item.drugCode ?? '',
  drugName: item.drugName ?? '',
  price: item.price ?? 0,
  outHospitalQuantity: item.outHospitalQuantity ?? 0,
  inHospitalQuantity: item.inHospitalQuantity ?? 0,
  pharmaCompanyId: item.pharmaCompanyId ?? 0,
  pharmaCompanyName: item.pharmaCompanyName ?? '',
  isDrugInfoExists: item.isDrugInfoExists ?? null,
  __tempRowKey: item.__tempRowKey ?? createTempRowKey('ocr'),
});

export const mapEditedOcrRowsToStagingDrugs = (
  rows: EditedOcrDrugRow[],
): StagingPrescriptionDrug[] =>
  rows.map((row) =>
    withPrescriptionTempRowKey({
      drugCode: row.drugCode ?? '',
      drugName: row.drugName ?? '',
      price: row.price ?? 0,
      outHospitalQuantity: row.outHospitalQuantity,
      inHospitalQuantity: row.inHospitalQuantity,
      pharmaCompanyId: row.pharmaCompanyId ?? 0,
      pharmaCompanyName: row.pharmaCompanyName ?? '',
      isDrugInfoExists: null,
    }),
  );

/** 레거시 plain OCR — drugCode 없는 행도 유지(품명 매칭으로 채움) */
export const mapOcrLinesToStagingDrugs = (
  lines: Array<{
    drugCode?: string;
    drugName?: string;
    outpatientUsageAmount?: number;
    inHouseUsageAmount?: number;
    price?: number | null;
    pharmaCompanyName?: string | null;
    pharmaCompanyId?: number | null;
  }>,
): StagingPrescriptionDrug[] =>
  lines.map((line) =>
    withPrescriptionTempRowKey({
      drugCode: line.drugCode ?? '',
      drugName: line.drugName ?? '',
      outHospitalQuantity: line.outpatientUsageAmount ?? 0,
      inHospitalQuantity: line.inHouseUsageAmount ?? 0,
      price: line.price ?? 0,
      pharmaCompanyId: line.pharmaCompanyId ?? 0,
      pharmaCompanyName: line.pharmaCompanyName ?? '',
      isDrugInfoExists: null,
    }),
  );

/**
 * 일괄 추가 후 서버가 isDrugInfoExists:false 로 준 행에
 * OCR/staging에서 얻은 품명·약가·제약사를 폴백 병합 (레거시 동일).
 */
export const mergeFetchedDrugsWithFallbackByDrugCode = (
  fetchedRows: PrescriptionDrugInfoResponse[],
  fallbackRows: StagingPrescriptionDrug[],
): PrescriptionDrugInfoResponse[] => {
  if (!Array.isArray(fetchedRows) || fetchedRows.length === 0) {
    return fetchedRows;
  }

  const fallbackRowMap = new Map<string, StagingPrescriptionDrug[]>();
  (fallbackRows || []).forEach((row) => {
    const drugCode = row?.drugCode;
    if (!drugCode) return;
    if (!fallbackRowMap.has(drugCode)) {
      fallbackRowMap.set(drugCode, []);
    }
    fallbackRowMap.get(drugCode)!.push(row);
  });

  return fetchedRows.map((row) => {
    if (row?.isDrugInfoExists !== false || !row?.drugCode) {
      return row;
    }

    const fallbackCandidates = fallbackRowMap.get(row.drugCode);
    const fallbackRow =
      Array.isArray(fallbackCandidates) && fallbackCandidates.length > 0
        ? fallbackCandidates.shift()
        : null;

    if (!fallbackRow) {
      return row;
    }

    return {
      ...row,
      drugName: fallbackRow.drugName ?? row.drugName ?? null,
      price: fallbackRow.price ?? row.price ?? null,
      pharmaCompanyName:
        fallbackRow.pharmaCompanyName ?? row.pharmaCompanyName ?? null,
      pharmaCompanyId:
        fallbackRow.pharmaCompanyId ?? row.pharmaCompanyId ?? null,
    };
  });
};

export interface DrugMatchingItem {
  drugName: string;
  drugCode?: string;
  pharmaCompanyName?: string;
  unitPrice?: number | null;
  inQuantity?: number;
  outQuantity?: number;
}

export interface DrugMatchingCandidate {
  drugCode?: string;
  drugName?: string;
  priceHira?: number;
  pharmaCompanyName?: string;
  pharmaCompanyId?: number;
}

export interface DrugMatchingResultItem {
  candidates?: DrugMatchingCandidate[];
}

export const applyDrugMatchingResults = (
  rows: StagingPrescriptionDrug[],
  results: DrugMatchingResultItem[],
): StagingPrescriptionDrug[] =>
  rows.map((row, index) => {
    const top = results[index]?.candidates?.[0];
    if (!top) return row;
    return {
      ...row,
      drugCode: top.drugCode ?? row.drugCode,
      drugName: top.drugName ?? row.drugName,
      price:
        top.priceHira != null && top.priceHira > 0 ? top.priceHira : row.price,
      pharmaCompanyName: top.pharmaCompanyName ?? row.pharmaCompanyName,
      pharmaCompanyId: top.pharmaCompanyId ?? row.pharmaCompanyId,
    };
  });
